import { useDisclosure, useToast } from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { hideAppSpinner, showAppSpinner } from "../../../../../../../context/Store/slices/appSpinnerSlice";
import { showErrorToast, showSuccessToast } from "../../../../../../../utils";
import CategoryForm
  from "../../../../../../OrganizationQuestionnaire/components/Categories/components/Category/CategoryForm";
import {
  CategoryGrid
} from "../../../../../../OrganizationQuestionnaire/components/Categories/components/CategoryGrid";
import { DeleteDialog } from "../../../../../../DataEntryPlanning/components/DeleteDialog";
import {
  TransportationAndDistributionModalWrapper
} from "../../../../../../OrganizationQuestionnaire/components/Categories/components/CategoryModalWrappers";
import * as React from "react";
import {
  useGetTransportTypes,
} from "../../../../../../../hooks/query";
import {
  mapTransportationAndDistributionApiData, mapTransportationAndDistributionFormData
} from "../../../../../../OrganizationQuestionnaire/components/Categories/components/Category/transportationAndDistributionHelpers";
import {
  supplierQuestionnaireUpstreamTransportationAndDistributionKeys,
  useAddSupplierQuestionnaireUpstreamTransportationAndDistributionItems,
  useDeleteSupplierQuestionnaireUpstreamTransportationAndDistributionItem,
  useGetSupplierQuestionnaireUpstreamTransportationAndDistributionItems,
  useUpdateSupplierQuestionnaireUpstreamTransportationAndDistributionItem
} from "../../../../../../../hooks/query/supplier-questionnaire/tabs/categories/upstream-transportation-and-distribution";

function CategoryFourConsumptionBased({ tabKey, currentElement, callMethod, isPreview }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { questionnaireId } = currentElement;
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const commonKey = "inputs.transportationAndDistribution";

  const {
    data: transportTypes
  } = useGetTransportTypes({
    onError: err => showErrorToast(toast, err.message)
  });

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const colHeaders = [
    t(`${commonKey}.typeOfTransport.name`),
    t(`${commonKey}.subtypeOfTransport.name`),
    t(`${commonKey}.subtypeTwo.name`),
    t(`${commonKey}.unit.name`),
    t(`${commonKey}.value`)
  ];
  const attributesOrder = ["typeOfTransport", "subtypeOfTransport", "subtypeTwo", "unit", "value"];
  const FORM_INPUT_KEY = "upstreamTransportationAndDistribution";

  const {
    handleSubmit
  } = useForm();

  const onSubmit = (() => {
    if (!isPreview) {
      callMethod(null);
    }
  });

  const prepareSectionValues = useCallback(items => {
    const typeOfTransportKey = `${commonKey}.typeOfTransport.options`;
    const subtypeOfTransportKey = `${commonKey}.subtypeOfTransport.options`;
    const subtypeTwoKey = `${commonKey}.subtypeTwo.options`;
    const unitKey = `${commonKey}.unit.options`;

    return mapTransportationAndDistributionApiData(items, transportTypes).map(item => {
      return {
        id: item.id ?? null,
        typeOfTransportValue: item.typeOfTransport,
        typeOfTransport: t(`${typeOfTransportKey}.${item.typeOfTransport}`, { defaultValue: item.typeOfTransport ?? "" }),
        subtypeOfTransportValue: item.subtypeOfTransport,
        subtypeOfTransport: t(`${subtypeOfTransportKey}.${item.subtypeOfTransport}`,
          { defaultValue: item.subtypeOfTransport ?? "" }),
        subtypeTwoValue: item.subtypeTwo,
        subtypeTwo: t(`${subtypeTwoKey}.${item.subtypeTwo}`, { defaultValue: item.subtypeTwo ?? "" }),
        unitValue: item.unit,
        unit: t(`${unitKey}.${item.unit}`, { defaultValue: item.unit ?? "" }),
        value: item.value ?? ""
      };
    });
  }, [t, transportTypes]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.upstreamTransportationAndDistribution.itemsType`)
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        typeOfTransport: item.typeOfTransportValue,
        subtypeOfTransport: item.subtypeOfTransportValue,
        subtypeTwo: item.subtypeTwoValue,
        unit: item.unitValue,
        value: item.value
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.upstreamTransportationAndDistribution.itemsType`)
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddSupplierQuestionnaireUpstreamTransportationAndDistributionItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(supplierQuestionnaireUpstreamTransportationAndDistributionKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateSupplierQuestionnaireUpstreamTransportationAndDistributionItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(supplierQuestionnaireUpstreamTransportationAndDistributionKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteSupplierQuestionnaireUpstreamTransportationAndDistributionItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(supplierQuestionnaireUpstreamTransportationAndDistributionKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleCategoryModalSubmit = (formData) => {
    const apiData = mapTransportationAndDistributionFormData(
      formData[categoryModalData.fieldArrayName], transportTypes
    );

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      typeOfTransport: "",
      subtypeOfTransport: "",
      subtypeTwo: "",
      unit: "",
      value: ""
    };
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={`navForm${tabKey}`} marginTop={4}>
      {transportTypes && <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetSupplierQuestionnaireUpstreamTransportationAndDistributionItems}
        isPreview={isPreview}
        maxHeight={"200"}
        showNoDataLabel={isPreview}
      />}

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />

      {
        isCategoryModalOpen && categoryModalData !== null && transportTypes && (
          <TransportationAndDistributionModalWrapper
            tabKey={"transportationAndDistribution"}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            getEmptyRow={getEmptyRow}
            transportTypes={transportTypes}
          />
        )
      }
    </CategoryForm>
  );
}

export default CategoryFourConsumptionBased;